import { create } from "zustand";
import type { FurnitureCatalog } from "../models/apiTypes";
import { type PlacementMode, DEFAULT_PLACEMENT } from "../domain/placement";

let _nextInstanceId = 1;

export interface PreviewItem {
  instanceId: number;
  catalogId: number;
  furniture: FurnitureCatalog;
  x: number;
  y: number;
  isMirrored: boolean;
  placement_mode: PlacementMode;
}

interface FurniturePreviewState {
  previewMapObjectId: number | null;
  previewItems: PreviewItem[];
  setPreviewFurniture: (mapObjectId: number, furniture: FurnitureCatalog) => void;
  updatePreviewPosition: (instanceId: number, x: number, y: number) => void;
  updatePreviewPlacementMode: (instanceId: number, placement_mode: PlacementMode) => void;
  updatePreviewCatalog: (instanceId: number, updatedCatalog: FurnitureCatalog) => void;
  togglePreviewMirror: (instanceId: number) => void;
  removePreviewItem: (instanceId: number) => void;
  /** Put back a try-on item exactly as it was (undo/redo of trying it on). */
  restorePreviewItem: (mapObjectId: number, item: PreviewItem) => void;
  clearPreviewFurniture: () => void;
}

const PREVIEW_CENTER_X = 0;
const PREVIEW_CENTER_Y = 250;

const useFurniturePreviewStore = create<FurniturePreviewState>((set) => ({
  previewMapObjectId: null,
  previewItems: [],

  setPreviewFurniture: (mapObjectId, furniture) =>
    set((state) => {
      const instanceId = _nextInstanceId++;
      const newItem: PreviewItem = {
        instanceId,
        catalogId: furniture.id,
        furniture,
        x: PREVIEW_CENTER_X,
        y: PREVIEW_CENTER_Y,
        isMirrored: false,
        placement_mode: DEFAULT_PLACEMENT,
      };
      // Previews belong to one house: trying something on in another house starts over.
      const kept = state.previewMapObjectId === mapObjectId ? state.previewItems : [];
      const newItems = [...kept, newItem];
      return {
        previewMapObjectId: mapObjectId,
        previewItems: newItems,
      };
    }),

  updatePreviewPosition: (instanceId, x, y) =>
    set((state) => ({
      previewItems: state.previewItems.map(p =>
        p.instanceId === instanceId ? { ...p, x, y } : p
      ),
    })),

  updatePreviewPlacementMode: (instanceId, placement_mode) =>
    set((state) => ({
      previewItems: state.previewItems.map(p =>
        p.instanceId === instanceId ? { ...p, placement_mode } : p
      ),
    })),

  updatePreviewCatalog: (instanceId, updatedCatalog) =>
    set((state) => ({
      previewItems: state.previewItems.map(p =>
        p.instanceId === instanceId ? { ...p, furniture: updatedCatalog } : p
      ),
    })),

  togglePreviewMirror: (instanceId) =>
    set((state) => ({
      previewItems: state.previewItems.map(p =>
        p.instanceId === instanceId ? { ...p, isMirrored: !p.isMirrored } : p
      ),
    })),

  removePreviewItem: (instanceId) =>
    set((state) => {
      const remaining = state.previewItems.filter(p => p.instanceId !== instanceId);
      return {
        previewItems: remaining,
        previewMapObjectId: remaining.length > 0 ? state.previewMapObjectId : null,
      };
    }),

  restorePreviewItem: (mapObjectId, item) =>
    set((state) => {
      const kept = state.previewMapObjectId === mapObjectId ? state.previewItems : [];
      if (kept.some((p) => p.instanceId === item.instanceId)) return state;
      return { previewMapObjectId: mapObjectId, previewItems: [...kept, item] };
    }),

  clearPreviewFurniture: () =>
    set({
      previewMapObjectId: null,
      previewItems: [],
    }),
}));

export default useFurniturePreviewStore;
